<script setup>
import { Link, router, usePage } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import UiTable from '@/Components/UI/UiTable.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import EmptyState from '@/Components/UI/EmptyState.vue'
import { useI18n } from 'vue-i18n'

defineOptions({ layout: AppLayout })

defineProps({
  hosts: { type: Array, default: () => [] }
})

const { t } = useI18n()
const page    = usePage()
const isAdmin = computed(() => page.props.auth?.user?.role === 'admin')

const search = ref('')

function doSearch() {
  router.get('/property_hosts', search.value ? { q: search.value } : {}, { preserveState: true, replace: true })
}

const columns = computed(() => [
  { key: 'name',       label: t('property_hosts.index.columns.name') },
  { key: 'phone',      label: t('property_hosts.index.columns.phone'), hideBelow: 'md' },
  { key: 'email',      label: t('property_hosts.index.columns.email'), hideBelow: 'lg' },
  { key: 'properties', label: t('property_hosts.index.columns.properties'), numeric: true, align: 'right' }
])

const rowHref    = (h) => `/property_hosts/${h.id}`
const onRowClick = (h) => router.visit(rowHref(h))
</script>

<template>
  <PageShell
    :title="t('property_hosts.index.title')"
    back-href="/"
    :back-label="t('common.back')"
  >
    <template #actions>
      <Link v-if="isAdmin" href="/property_hosts/new">
        <UiButton variant="primary" icon-left="plus">
          {{ t('property_hosts.index.new') }}
        </UiButton>
      </Link>
    </template>

    <template #filters>
      <UiInput
        v-model="search"
        :placeholder="t('property_hosts.index.search_placeholder')"
        icon-left="search"
        @keyup.enter="doSearch"
        @blur="doSearch"
      />
    </template>

    <EmptyState
      v-if="hosts.length === 0"
      type="first-use"
      :title="t('property_hosts.index.empty')"
    />

    <UiTable
      v-else
      :rows="hosts"
      :columns="columns"
      :row-href="rowHref"
      @row-click="onRowClick"
    >
      <template #cell-name="{ row }">
        <span class="font-medium text-[color:var(--ink)]">{{ row.name }}</span>
      </template>

      <template #cell-phone="{ row }">
        <span class="text-sm text-[color:var(--inkSoft)]">{{ row.contact_phone || '—' }}</span>
      </template>

      <template #cell-email="{ row }">
        <span class="text-sm text-[color:var(--inkSoft)]">{{ row.contact_email || '—' }}</span>
      </template>

      <template #cell-properties="{ row }">
        <span class="fw-value tabular-nums">{{ row.properties_count }}</span>
        <span
          v-if="row.pending_count > 0"
          class="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full"
          :style="{ background: 'color-mix(in oklch, var(--warn) 14%, var(--surface))', color: 'var(--warn)' }"
        >
          {{ row.pending_count }} pending
        </span>
      </template>
    </UiTable>
  </PageShell>
</template>
